// application/useVisitaForm.js  (carga por uuid + guardado orquestado). Sin JSX, sin Swal.
import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { visitasRepository } from '../infrastructure/visitas.repository';
import { mediaRepository } from '../../shared/infrastructure/media.repository';
import { guardarConHijos, reintentarImagen } from '../../shared/application/guardarConHijos';
import { createVisita } from '../domain/visita';
import { toApiDate, toApiDateTime } from '../../shared/domain/fecha';
import { DomainError } from '../../shared/domain/DomainError';
import { asistentesFinales, asistentesVigentes, construirAsistentesPayload } from '../../shared/domain/asistentes';

const TIPO = 'visita';
const MSG_SIN_PRODUCTOR = 'El productor es requerido';

// uuid presente => modo edición. El uuid nuevo y la fecha de hoy se generan AQUÍ (application), no en domain.
export const useVisitaForm = ({ uuid, repository = visitasRepository, media = mediaRepository } = {}) => {
  const isEdit = !!uuid;
  const [state, setState] = useState({ data: null, status: isEdit ? 'loading' : 'success', error: null });
  const [saving, setSaving] = useState(false);
  const mounted = useRef(true);
  // Asistentes vigentes [{ uuid, id_cuenta }] conocidos (cargados o devueltos por el último guardado): se reenvía su uuid.
  const asistentesRef = useRef([]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isEdit) {
      asistentesRef.current = [];
      setState({
        data: createVisita({ uuid: uuidv4(), fecha: toApiDate(new Date()) }),
        status: 'success',
        error: null,
      });
      return;
    }
    let cancelled = false;
    setState({ data: null, status: 'loading', error: null });
    repository.get(uuid).then(
      data => {
        if (!mounted.current || cancelled) return;
        asistentesRef.current = asistentesVigentes(data.asistentes);
        setState({ data, status: 'success', error: null });
      },
      error => mounted.current && !cancelled && setState({ data: null, status: 'error', error })
    );
    return () => {
      cancelled = true;
    };
  }, [uuid, isEdit, repository]);

  /**
   * `values` trae `id_cuenta` (el productor visitado = único asistente de la visita, no es campo de la visita): viaja como
   * lista de un elemento en `asistentes` del mismo payload (reusa su uuid si es la misma cuenta).
   * Orquesta (guardarConHijos): visita -> eliminar imágenes marcadas -> subir imágenes pendientes.
   * `extras`: { pendientes: [{uuid, file}], eliminaciones: [uuid], onImagenEstado, onEliminacionEstado }.
   * Devuelve { guardado, imagenes, eliminaciones } (un fallo de imagen no lanza); lanza DomainError si falla la visita.
   */
  const save = useCallback(
    async ({ id_cuenta: idCuenta, ...values }, { pendientes = [], eliminaciones = [], onImagenEstado, onEliminacionEstado } = {}) => {
      const creado = toApiDateTime(new Date());
      // Dejar la visita sin productor quitaría su único asistente: no se permite (como antes).
      if (!idCuenta && asistentesRef.current.length) throw new DomainError(MSG_SIN_PRODUCTOR);
      const payloadAsistentes = construirAsistentesPayload(idCuenta ? [Number(idCuenta)] : [], asistentesRef.current, uuidv4);

      setSaving(true);
      try {
        return await guardarConHijos({
          tipo: TIPO,
          entidad: 'la visita',
          guardarPadre: async () => {
            const entity = createVisita({ ...state.data, ...values });
            const guardada = await (isEdit
              ? repository.update(entity, { asistentes: payloadAsistentes })
              : repository.create(entity, { asistentes: payloadAsistentes }));
            asistentesRef.current = asistentesFinales(guardada, payloadAsistentes);
            return guardada;
          },
          media,
          pendientes,
          eliminaciones,
          creado,
          onImagenEstado,
          onEliminacionEstado,
        });
      } finally {
        if (mounted.current) setSaving(false);
      }
    },
    [state.data, isEdit, repository, media]
  );

  /** Reintenta solo UNA imagen contra la entidad ya guardada (usa su `id` numérico); no vuelve a guardar el formulario. */
  const retryImagen = useCallback(
    (item, onImagenEstado) => {
      if (!state.data || !state.data.id) return Promise.resolve(null);
      return reintentarImagen({ tipo: TIPO, idPadre: state.data.id, item, media, creado: toApiDateTime(new Date()), onImagenEstado });
    },
    [state.data, media]
  );

  return {
    data: state.data,
    status: state.status,
    error: state.error,
    isEdit,
    saving,
    save,
    reintentarImagen: retryImagen,
  };
};

export default useVisitaForm;
